import { useBetsByRound } from '@/api/bets';
import { useBetBySlug } from '@/api/bets/queries/useBetBySlug';
import { useGameRoundsBySlug } from '@/api/game-round';
import games from '@/constants/games';
import { OrderByOptions } from '@/enums/order-by';
import { DetailedBet } from '@/models/detailed-bet';
import { DetailedGameRound } from '@/models/detailed-round';
import { useGameReportSearchParams } from '../useGameReportSearchParams';
import { GameReportType } from '../useGameReportSearchParams/gameReportType.enum';
import { UseGameReportDataReturn } from './props';

export function useGameReportData(): UseGameReportDataReturn {
  const { betSlug, roundSlug, roundId, type, gameId } =
    useGameReportSearchParams();

  const game = games.get(gameId?.toString())!;

  const {
    round,
    error: errorRoundBySlug,
    isPending: isPendingRoundBySlug,
    refetch: refetchRoundBySlug,
    isFetching: isFetchingRoundBySlug,
  } = useGameRoundsBySlug({
    slug: roundSlug as string,
  });

  const {
    data: bets,
    isPending: isPendingBets,
    error: errorBets,
    refetch: refetchBets,
    isFetching: isFetchingBets,
  } = useBetsByRound({
    type: 'infinity',
    gameRoundId: roundId as string,
    enabled: type === GameReportType.RoundGame,
    sortOrder: OrderByOptions.Descending,
  });

  const {
    data: bet,
    error: errorBet,
    refetch: refetchBet,
    isPending: isPendingBet,
    isFetching: isFetchingBet,
  } = useBetBySlug({
    slug: betSlug as string,
  });

  function handleRoundGameError() {
    if (errorRoundBySlug) {
      refetchRoundBySlug();
    }

    if (errorBets) {
      refetchBets();
    }
  }

  function getState(): UseGameReportDataReturn {
    switch (type) {
      case GameReportType.RoundGame:
        return {
          game,
          bets: bets as DetailedBet[],
          error: errorRoundBySlug ?? errorBets,
          isPending: isPendingRoundBySlug || isPendingBets,
          refetch: handleRoundGameError,
          isFetching: isFetchingRoundBySlug || isFetchingBets,
          randomness: round?.randomness,
          round: round as DetailedGameRound,
        };
      case GameReportType.BetGame:
        return {
          bets: !!bet ? [bet] : [],
          game,
          round: null,
          error: errorBet,
          refetch: refetchBet,
          isPending: isPendingBet,
          isFetching: isFetchingBet,
          randomness: bet?.randomness,
        };
      default:
        return {
          bets: [],
          game,
          round: null,
          error: null,
          refetch: () => {},
          isPending: false,
          isFetching: false,
          randomness: null,
        };
    }
  }

  return getState();
}
